import React, { useState, useCallback, useEffect } from 'react';
import { request } from '../api/request';
import { buildTextGenerationUrl } from '../utils/aiEndpoints';

interface AITextGenerationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onTextSelect: (text: string) => void;
  defaultPrompt?: string;
  currentFieldContent?: string;
}

export const AITextGenerationDrawer: React.FC<AITextGenerationDrawerProps> = ({
  isOpen,
  onClose,
  onTextSelect,
  defaultPrompt = '',
  currentFieldContent = ''
}) => {
  // Local state instead of global Redux state
  const [generatedText, setGeneratedText] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState(defaultPrompt || '');

  // Smart prompt generation based on current field content and context
  const generateSmartPrompt = useCallback(() => {
    // Strip HTML tags from current content for cleaner prompt
    const cleanContent = currentFieldContent?.replace(/<[^>]*>/g, '').trim();
    
    if (cleanContent && cleanContent.length > 0) {
      // Return just the clean content without any prefix text
      return cleanContent;
    }
    
    // Fallback to context-based or generic prompts
    return defaultPrompt || '';
  }, [currentFieldContent, defaultPrompt]);

  // Auto-populate prompt when drawer opens with smart prompt generation
  useEffect(() => {
    if (isOpen) {
      const smartPrompt = generateSmartPrompt();
      if (smartPrompt) {
        setPrompt(smartPrompt);
      } else if (defaultPrompt) {
        setPrompt(defaultPrompt);
      }
    }
  }, [isOpen, generateSmartPrompt, defaultPrompt]);

  // Clear state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setGeneratedText('');
      setError(null);
      setPrompt(defaultPrompt || '');
    }
  }, [isOpen, defaultPrompt]);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const { url, base } = buildTextGenerationUrl(prompt);
      const response = await request({
        method: 'GET',
        url,
        base,
        requiresAuth: false,
        requiresRestaurantId: false,
        timeout:60000
      });

      // Handle different response formats for text
      let texts: string[] = [];
      
      if (Array.isArray(response?.data)) {
        // Direct array response
        texts = response.data;
      } else if (Array.isArray((response?.data as any)?.texts)) {
        // Nested texts property
        texts = (response.data as any).texts;
      } else if (Array.isArray(response)) {
        // Response is directly an array
        texts = response;
      } else if (typeof response?.data === 'string') {
        // Single text response
        texts = [response.data];
      } else if (typeof response === 'string') {
        // Direct string response
        texts = [response];
      }

      if (!texts || !Array.isArray(texts) || texts.length === 0) {
        setError('No text generated. Please try again.');
        return;
      }

      // Take the first generated text
      setGeneratedText(texts[0] || '');
    } catch (err: any) {
      let errorMessage = 'Failed to generate text';
      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [prompt]);

  const handleTextSelect = useCallback((text: string) => {
    onTextSelect(text);
    onClose();
  }, [onTextSelect, onClose]);

  const handleClose = useCallback(() => {
    setGeneratedText('');
    setError(null);
    setPrompt(defaultPrompt || '');
    onClose();
  }, [onClose, defaultPrompt]);


  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 transition-opacity duration-300 bg-black/30"
          onClick={handleClose}
        />
      )}

      {/* Drawer */}
      <div 
        className={`fixed top-0 right-0 h-full w-96 max-w-full bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
            <h2 className="text-lg font-semibold text-gray-900">Generate Text with AI</h2>
            <button 
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Prompt Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe the text you want to generate
              </label>
              <div className="space-y-3">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g., Write a catchy restaurant slogan, Create a menu description for pizza"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                  rows={3}
                  disabled={loading}
                  autoFocus
                />
                <button
                  onClick={handleGenerate}
                  disabled={loading || !prompt.trim()}
                  className="w-full px-4 py-3 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-red-600 hover:bg-red-700 disabled:bg-gray-400"
                >
                  {loading ? 'Generating...' : 'Generate Text'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Be specific about the type of text you need - style, tone, and purpose
              </p>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center py-8">
                <div className="text-center">
                  <div className="relative">
                    <div className="w-10 h-10 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1">
                    <p className="text-sm font-medium text-red-600">Generating text...</p>
                    <p className="text-xs text-gray-500">This may take a few moments</p>
                  </div>
                  {/* Progress Bar */}
                  <div className="w-full max-w-xs bg-gray-200 rounded-full h-2 mt-3 mx-auto">
                    <div className="h-2 rounded-full animate-pulse bg-red-600 w-3/4"></div>
                  </div>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-600 font-medium text-sm">Generation Failed</p>
                </div>
                <p className="text-red-600 mt-1 text-sm">{error}</p>
                <button
                  onClick={handleGenerate}
                  disabled={loading || !prompt.trim()}
                  className="mt-2 px-3 py-1 bg-red-500 text-white text-sm rounded-md hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Generated Text Display */}
            {generatedText && (
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3">
                  Generated Text
                </h3>
                <div className="rounded-lg border-2 border-gray-200 bg-white p-4">
                  <div className="text-sm text-gray-800 leading-relaxed">
                    {generatedText}
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && !generatedText && (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-base font-medium text-gray-900 mb-2">Ready to Generate</h3>
                <p className="text-gray-500 mb-3 text-sm">Enter a description above and click Generate to create AI text</p>
                <div className="text-xs text-gray-400">
                  <p className="font-medium">💡 Tips for better results:</p>
                  <ul className="mt-2 space-y-1 text-left">
                    <li>• Specify the type of text (slogan, description, etc.)</li>
                    <li>• Include tone and style preferences</li>
                    <li>• Mention your target audience</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Save Button Footer */}
          {generatedText && (
            <div className="border-t border-gray-200 p-4 bg-white">
              <button
                onClick={() => handleTextSelect(generatedText)}
                className="w-full px-4 py-3 text-white font-semibold rounded-md transition-colors bg-red-600 hover:bg-red-700"
              >
                Save
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

AITextGenerationDrawer.displayName = 'AITextGenerationDrawer';
